// FE-SCRM-053 — Sales Contact TanStack Query hooks.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  fetchContactDetail,
  fetchContactRoles,
  fetchContactsList,
  patchContact,
} from "./api";
import type {
  ContactListQuery,
  ContactListResponse,
  ContactPatchInput,
  ContactRolesResponse,
  SalesContact,
} from "./types";


export const CONTACTS_QUERY_KEY = "sales-contacts" as const;
export const CONTACT_DETAIL_QUERY_KEY = "sales-contact-detail" as const;
export const CONTACT_ROLES_QUERY_KEY = "sales-contact-roles" as const;


export function useContactsListQuery(
  query: ContactListQuery,
): UseQueryResult<ContactListResponse> {
  return useQuery({
    queryKey: [CONTACTS_QUERY_KEY, "list", query],
    queryFn: ({ signal }) => fetchContactsList(query, { signal }),
  });
}


export function useContactDetailQuery(
  contact_id: string | undefined,
): UseQueryResult<SalesContact> {
  return useQuery({
    queryKey: [CONTACT_DETAIL_QUERY_KEY, contact_id],
    queryFn: ({ signal }) => fetchContactDetail(contact_id!, { signal }),
    enabled: Boolean(contact_id),
  });
}


export function useContactRolesQuery(
  contact_id: string | undefined,
): UseQueryResult<ContactRolesResponse> {
  return useQuery({
    queryKey: [CONTACT_ROLES_QUERY_KEY, contact_id],
    queryFn: ({ signal }) => fetchContactRoles(contact_id!, { signal }),
    enabled: Boolean(contact_id),
  });
}


export interface PatchContactVariables {
  contact_id: string;
  patch: ContactPatchInput;
}


export function usePatchContactMutation(): UseMutationResult<
  SalesContact, Error, PatchContactVariables
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: PatchContactVariables) =>
      patchContact(vars.contact_id, vars.patch),
    onSuccess: (next) => {
      // Refresh the detail page + every list cache (the list row
      // mirrors fields the user might have edited).
      qc.setQueryData(
        [CONTACT_DETAIL_QUERY_KEY, next.contact_id], next,
      );
      qc.invalidateQueries({ queryKey: [CONTACTS_QUERY_KEY, "list"] });
    },
  });
}
